import { useCallback, useEffect, useState } from "react";
import { IIdmStaffMember, idmService } from "../../../entities/idm";
import { IUserPersonalData, userService } from "../../../entities/user";
import { MyError } from "../../../shared/lib/error/MyError";
import { emptyStaffMember } from "./emptyStaffMember";
import { useLocation, useNavigate } from 'react-router-dom';
import { getStaffDetailPath, getStaffSection, isStaffDetailPath } from '../../../app/router/staffNavigation';

interface IUseStaffMemberParams {
    staffId: number | null;
    onError: (error: unknown, defaultMessage: string) => void;
    onNotFound: () => void;
}

export const useStaffMember = ({staffId, onError, onNotFound}: IUseStaffMemberParams) => {
    const navigate = useNavigate()
    const {pathname, search} = useLocation()
    const [staffMember, setStaffMember] = useState<IIdmStaffMember>(emptyStaffMember)
    const [userPersonalData, setUserPersonalData] = useState<IUserPersonalData | null>(null)
    const [isLoading, setIsLoading] = useState(Boolean(staffId))
    const [loadError, setLoadError] = useState('')

    useEffect(() => {
        if(!staffId || staffMember.user_id !== staffId || !isStaffDetailPath(pathname, staffId)) return
        const section = staffMember.roles.some(role => role.role_code === 'tutor') ? 'performers' : 'workers'
        if(getStaffSection(pathname) !== section){
            const suffix = pathname.match(/\/(idm|history|payment-details)$/)?.[0] || ''
            navigate(getStaffDetailPath(staffId, section, search, suffix), {replace: true})
        }
    }, [navigate, pathname, search, staffId, staffMember])

    const loadStaffMember = useCallback(async () => {
        if(!staffId){
            setStaffMember(emptyStaffMember)
            setUserPersonalData(null)
            setLoadError('')
            setIsLoading(false)
            return
        }

        try{
            setIsLoading(true)
            setLoadError('')
            const [staffResponse, personalResponse] = await Promise.allSettled([
                idmService.getStaffMember(staffId),
                userService.getPersonalData(staffId),
            ])

            if(staffResponse.status !== 'fulfilled'){
                throw staffResponse.reason
            }

            setStaffMember(staffResponse.value)
            setUserPersonalData(personalResponse.status === 'fulfilled' ? personalResponse.value : null)
        }
        catch(error){
            setStaffMember(emptyStaffMember)
            setUserPersonalData(null)

            if(error instanceof MyError && (error.status === 404 || error.status >= 500)){
                onNotFound()
            }
            else{
                setLoadError(
                    error instanceof MyError && error.status === 403
                        ? 'Нет доступа к карточке сотрудника'
                        : 'Не удалось загрузить карточку сотрудника'
                )
                onError(error, 'Ошибка при получении карточки сотрудника')
            }
        }
        finally{
            setIsLoading(false)
        }
    }, [onError, onNotFound, staffId])

    useEffect(() => {
        loadStaffMember()
    }, [loadStaffMember])

    return {
        isLoading,
        loadError,
        loadStaffMember,
        setStaffMember,
        staffMember,
        userPersonalData,
    }
}
